import { useTranslation } from 'react-i18next'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Home,
  CalendarDays,
  BookOpen,
  ShoppingBasket,
  ShoppingCart,
  MoreHorizontal,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTabBadges } from '@/shared/hooks/useTabBadges'
import { TabBadge } from './TabBadge'

interface NavTab {
  path: string
  labelKey: string
  icon: React.ComponentType<{ className?: string; fill?: string }>
  badgeKey?: 'plan' | 'pantry'
}

export const TABS: NavTab[] = [
  { path: '/home', labelKey: 'nav.home', icon: Home },
  { path: '/plan', labelKey: 'nav.plan', icon: CalendarDays, badgeKey: 'plan' },
  { path: '/recipes', labelKey: 'nav.recipes', icon: BookOpen },
  { path: '/pantry', labelKey: 'nav.pantry', icon: ShoppingBasket, badgeKey: 'pantry' },
  { path: '/grocery-list', labelKey: 'nav.groceryList', icon: ShoppingCart },
  { path: '/more', labelKey: 'nav.more', icon: MoreHorizontal },
]

export function BottomNav() {
  const { t } = useTranslation('shell')
  const currentPath = useRouterState({ select: (s) => s.location.pathname })
  const badges = useTabBadges()

  return (
    <nav
      aria-label={t('nav.ariaLabel')}
      className="fixed bottom-0 left-0 right-0 z-40 bg-canvas border-t border-line print:hidden"
    >
      <ul className="flex items-center justify-around h-[60px]" role="list">
        {TABS.map(({ path, labelKey, icon: Icon, badgeKey }) => {
          const isActive = currentPath.startsWith(path)
          const badgeCount = badgeKey && !badges.isLoading ? badges[badgeKey] : null
          const ariaLabel = badgeCount
            ? t('nav.badgeLabel', { label: t(labelKey), count: badgeCount })
            : t(labelKey)

          return (
            <li key={path} className="flex-1">
              <Link
                to={path}
                aria-current={isActive ? 'page' : undefined}
                aria-label={ariaLabel}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5',
                  'min-h-[44px] min-w-[44px] w-full h-full',
                  'text-xs font-medium motion-safe:transition-colors',
                  isActive ? 'text-accent' : 'text-muted',
                )}
              >
                <div className="relative">
                  <Icon
                    className="h-5 w-5"
                    fill={isActive ? 'currentColor' : 'none'}
                    aria-hidden="true"
                  />
                  {badgeCount && <TabBadge count={badgeCount} />}
                </div>
                <span>{t(labelKey)}</span>
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
